import { z } from 'zod';
import { ModuleManifest, ModuleResultPublic } from '@momentum/shared';
import { POC_CHOICE_ID } from '../poc-choice/manifest.ts';

/**
 * DISPOSABLE fixture. Proves: server-resolved die recorded exactly once per accepted action, module-defined round
 * membership with a frozen roster, per-player turn completion in any order, exactly one automatic child launch when
 * the round completes, roster reconciliation on removal, and board-owned progress. Dice and movement are module rules.
 */
export const FIXTURE_DICE_BOARD_ID = 'fixture-dice-board';

export const fixtureDiceBoardManifest = ModuleManifest.parse({
  id: FIXTURE_DICE_BOARD_ID,
  version: '0.1.0',
  name: 'Dice round fixture (disposable)',
  description: 'Everyone rolls once per round; the round completion auto-launches one choice test.',
  disposable: true,
  players: { min: 2, max: 6 },
  participation: 'individual',
  teams: null,
  voice: { scope: 'shared' },
  capabilities: { launchesChildren: true, childModuleIds: [POC_CHOICE_ID], usesRandom: true, hasPrivateData: false, renderer: 'board' },
  launchableFromLobby: true,
});

export const FixtureDiceBoardConfig = z.object({
  rounds: z.number().int().min(1).max(10).default(2),
  sides: z.number().int().min(2).max(20).default(6),
  childConfig: z.unknown().optional(),
});
export type FixtureDiceBoardConfig = z.infer<typeof FixtureDiceBoardConfig>;

export const FixtureDiceBoardAction = z.discriminatedUnion('type', [
  /** Roll once per round; the server resolves the value, moves the token along `connections[0]` and records both. */
  z.object({ type: z.literal('roll') }),
]);
export type FixtureDiceBoardAction = z.infer<typeof FixtureDiceBoardAction>;

export const FixtureDiceBoardResultPayload = z.object({
  schema: z.literal(1),
  roundsCompleted: z.number().int(),
  rollsPerParticipant: z.record(z.string(), z.number().int()),
  distance: z.record(z.string(), z.number().int()),
  childResults: z.array(ModuleResultPublic),
});
export type FixtureDiceBoardResultPayload = z.infer<typeof FixtureDiceBoardResultPayload>;
